import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { generateLRC, downloadLRC, generateLRCWithWhisper, generateLRCWithWebSpeech, generateLRCWithTimeEstimation, validateOpenAIKey } from '../utils/lrcGenerator';

const Container = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    color: white;
`;

const ContentWrapper = styled.div`
    max-width: 800px;
    margin: 0 auto;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    padding: 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px) {
        padding: 30px 20px;
    }
`;

const Title = styled.h2`
    font-size: 2.5rem;
    margin-bottom: 10px;
    color: #1a1a1a;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    color: #666;
    margin-bottom: 30px;
    font-size: 1rem;
    line-height: 1.6;
`;

const Section = styled.div`
    margin-bottom: 30px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #1a1a1a;
    font-size: 0.95rem;
`;

const Input = styled.input`
    width: 100%;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s;

    &:focus {
        outline: none;
        border-color: #2a5298;
    }
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 200px;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.3s;

    &:focus {
        outline: none;
        border-color: #2a5298;
    }
`;

const FileInput = styled.input`
    display: none;
`;

const Button = styled.button`
    padding: 12px 24px;
    background: ${props => props.variant === 'secondary' ? '#6c757d' : '#1DB954'};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s;
    margin-right: 10px;

    &:hover {
        background: ${props => props.variant === 'secondary' ? '#5a6268' : '#1ed760'};
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const Select = styled.select`
    width: 100%;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    cursor: pointer;
    transition: border-color 0.3s;

    &:focus {
        outline: none;
        border-color: #2a5298;
    }
`;

const StatusMessage = styled.div`
    padding: 12px;
    border-radius: 8px;
    margin-top: 15px;
    background: ${props => {
        if (props.type === 'error') return '#fee';
        if (props.type === 'success') return '#efe';
        return '#eef';
    }};
    color: ${props => {
        if (props.type === 'error') return '#c33';
        if (props.type === 'success') return '#3c3';
        return '#33c';
    }};
    border: 1px solid ${props => {
        if (props.type === 'error') return '#fcc';
        if (props.type === 'success') return '#cfc';
        return '#ccf';
    }};
`;

const LoadingSpinner = styled.div`
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s ease-in-out infinite;
    margin-right: 10px;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

const Preview = styled.div`
    margin-top: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    max-height: 300px;
    overflow-y: auto;
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    white-space: pre-wrap;
`;

const DropZone = styled.div`
    border: 3px dashed ${props => props.isDragging ? '#2a5298' : '#e0e0e0'};
    border-radius: 12px;
    padding: 40px 20px;
    text-align: center;
    background: ${props => props.isDragging ? 'rgba(42, 82, 152, 0.05)' : '#fafafa'};
    transition: all 0.3s ease;
    cursor: pointer;
    margin-bottom: 15px;

    &:hover {
        border-color: #2a5298;
        background: rgba(42, 82, 152, 0.05);
    }
`;

const DropZoneIcon = styled.div`
    font-size: 3rem;
    margin-bottom: 15px;
    opacity: ${props => props.isDragging ? 1 : 0.6};
    transition: opacity 0.3s ease;
`;

const DropZoneText = styled.div`
    font-size: 1.1rem;
    color: #333;
    font-weight: 600;
    margin-bottom: 8px;
`;

const DropZoneHint = styled.div`
    font-size: 0.9rem;
    color: #666;
    margin-top: 8px;
`;

const FileInfo = styled.div`
    margin-top: 10px;
    padding: 10px;
    background: #e8f4f8;
    border-radius: 8px;
    font-size: 0.9rem;
    color: #2a5298;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const FileIcon = styled.span`
    font-size: 1.2rem;
`;

const LRCGeneratorPage = () => {
    const [audioFile, setAudioFile] = useState(null);
    const [lyricsText, setLyricsText] = useState('');
    const [method, setMethod] = useState('whisper'); // 'whisper' or 'webspeech'
    const [apiKey, setApiKey] = useState('');
    const [title, setTitle] = useState('');
    const [artist, setArtist] = useState('');
    const [album, setAlbum] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isProcessing, setIsProcessing] = useState(false);
    const [preview, setPreview] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [dragType, setDragType] = useState(null); // 'audio' or 'lyrics'
    const fileInputRef = useRef(null);
    const lyricsFileInputRef = useRef(null);
    const audioDropZoneRef = useRef(null);
    const lyricsDropZoneRef = useRef(null);
    const [isValidatingKey, setIsValidatingKey] = useState(false);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('audio/')) {
            setAudioFile(file);
            setStatus({ type: 'info', message: `已選擇音頻文件: ${file.name}` });
        } else {
            setStatus({ type: 'error', message: '請選擇有效的音頻文件' });
        }
    };

    const loadLyricsFromFile = async (file) => {
        const text = await file.text();
        setLyricsText(text);
        setStatus({ type: 'success', message: '歌詞文件已載入' });
    };

    // 拖放處理函數
    const handleDragEnter = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragType(type);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 只有當離開整個拖放區域時才取消高亮
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
            setDragType(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragType(null);

        const files = Array.from(e.dataTransfer.files);
        
        if (type === 'audio') {
            const audioFile = files.find(file => file.type.startsWith('audio/'));
            if (audioFile) {
                setAudioFile(audioFile);
                setStatus({ type: 'info', message: `已拖放音頻文件: ${audioFile.name}` });
            } else {
                setStatus({ type: 'error', message: '請拖放有效的音頻文件' });
            }
        } else if (type === 'lyrics') {
            const textFile = files.find(file => 
                file.type === 'text/plain' || 
                file.name.endsWith('.txt') || 
                file.name.endsWith('.md')
            );
            if (textFile) {
                await loadLyricsFromFile(textFile);
            } else {
                setStatus({ type: 'error', message: '請拖放有效的文本文件 (.txt, .md)' });
            }
        }
    };

    // 全局拖放事件處理（防止瀏覽器默認行為）
    React.useEffect(() => {
        const handleGlobalDragOver = (e) => {
            e.preventDefault();
        };

        const handleGlobalDrop = (e) => {
            e.preventDefault();
        };

        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('drop', handleGlobalDrop);

        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('drop', handleGlobalDrop);
        };
    }, []);

    const handleGenerate = async () => {
        if (!audioFile) {
            setStatus({ type: 'error', message: '請先選擇音頻文件' });
            return;
        }

        if (!lyricsText.trim()) {
            setStatus({ type: 'error', message: '請輸入或載入歌詞文本' });
            return;
        }

        if (method === 'whisper' && !apiKey.trim()) {
            setStatus({ type: 'error', message: '使用 Whisper API 需要提供 OpenAI API Key' });
            return;
        }

        if (method === 'webspeech') {
            setStatus({ type: 'error', message: 'Web Speech API 無法直接處理音頻文件，請選擇其他方法' });
            return;
        }

        setIsProcessing(true);
        setStatus({ type: 'info', message: '正在處理，請稍候...' });

        try {
            let alignedLyrics;

            if (method === 'whisper') {
                alignedLyrics = await generateLRCWithWhisper(audioFile, lyricsText, apiKey);
            } else if (method === 'estimation') {
                alignedLyrics = await generateLRCWithTimeEstimation(audioFile, lyricsText);
            } else {
                alignedLyrics = await generateLRCWithWebSpeech(audioFile, lyricsText);
            }

            const lrcContent = generateLRC({
                lyrics: alignedLyrics,
                title,
                artist,
                album
            });

            setPreview(lrcContent);
            setStatus({ 
                type: 'success', 
                message: method === 'estimation' 
                    ? 'LRC 文件生成成功！（使用時間估算方法，建議檢查並手動調整時間戳）' 
                    : 'LRC 文件生成成功！' 
            });
        } catch (error) {
            let errorMessage = error.message;
            
            // 提供更友好的錯誤提示
            if (errorMessage.includes('no-speech') || errorMessage.includes('Web Speech API')) {
                errorMessage = 'Web Speech API 無法處理音頻文件。建議使用 OpenAI Whisper API（需要 API Key）或時間估算方法（簡單快速）。';
            } else if (errorMessage.includes('Whisper API')) {
                errorMessage = `Whisper API 錯誤: ${errorMessage}。請檢查 API Key 是否正確，或嘗試使用時間估算方法。`;
            }
            
            setStatus({ type: 'error', message: `生成失敗: ${errorMessage}` });
            console.error('Error generating LRC:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleValidateApiKey = async () => {
        if (!apiKey.trim()) {
            setStatus({ type: 'error', message: '請先輸入 API Key 再點擊驗證' });
            return;
        }

        setIsValidatingKey(true);
        setStatus({ type: 'info', message: '正在驗證 API Key，請稍候…' });

        try {
            await validateOpenAIKey(apiKey);
            setStatus({ type: 'success', message: '✅ API Key 看起來是有效的，可以使用 Whisper。' });
        } catch (error) {
            setStatus({ type: 'error', message: `API Key 驗證失敗：${error.message}` });
        } finally {
            setIsValidatingKey(false);
        }
    };

    const handleDownload = () => {
        if (!preview) {
            setStatus({ type: 'error', message: '沒有可下載的內容' });
            return;
        }

        const filename = title || 'lyrics';
        downloadLRC(preview, filename);
        setStatus({ type: 'success', message: 'LRC 文件已下載' });
    };

    return (
        <Container>
            <ContentWrapper>
                <Title>🎵 AI LRC Generator</Title>
                <Subtitle>使用 AI 自動生成帶時間戳的 LRC 歌詞文件。支持 OpenAI Whisper API 和 Web Speech API，可以從音頻文件和歌詞文本自動對齊生成 LRC 格式的歌詞文件。</Subtitle>

            <Section>
                <Label>1. 選擇音頻文件 (MP3, WAV, etc.)</Label>
                <DropZone
                    ref={audioDropZoneRef}
                    isDragging={isDragging && dragType === 'audio'}
                    onDragEnter={(e) => handleDragEnter(e, 'audio')}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'audio')}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <DropZoneIcon isDragging={isDragging && dragType === 'audio'}>
                        🎵
                    </DropZoneIcon>
                    <DropZoneText>
                        {isDragging && dragType === 'audio' ? '放開以上傳音頻文件' : '拖放音頻文件到這裡'}
                    </DropZoneText>
                    <DropZoneHint>或點擊選擇文件</DropZoneHint>
                    {audioFile && (
                        <FileInfo>
                            <FileIcon>📁</FileIcon>
                            <span>{audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </FileInfo>
                    )}
                </DropZone>
                <FileInput
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleFileSelect}
                />
            </Section>

            <Section>
                <Label>2. 輸入或載入歌詞文本</Label>
                <DropZone
                    ref={lyricsDropZoneRef}
                    isDragging={isDragging && dragType === 'lyrics'}
                    onDragEnter={(e) => handleDragEnter(e, 'lyrics')}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'lyrics')}
                    onClick={() => lyricsFileInputRef.current?.click()}
                    style={{ marginBottom: '15px' }}
                >
                    <DropZoneIcon isDragging={isDragging && dragType === 'lyrics'}>
                        📄
                    </DropZoneIcon>
                    <DropZoneText>
                        {isDragging && dragType === 'lyrics' ? '放開以上傳歌詞文件' : '拖放歌詞文件到這裡 (.txt, .md)'}
                    </DropZoneText>
                    <DropZoneHint>或點擊選擇文件</DropZoneHint>
                </DropZone>
                <FileInput
                    ref={lyricsFileInputRef}
                    type="file"
                    accept=".txt,.md"
                    onChange={(e) => {
                        if (e.target.files[0]) {
                            loadLyricsFromFile(e.target.files[0]);
                        }
                    }}
                    id="lyrics-file-input"
                />
                <TextArea
                    value={lyricsText}
                    onChange={(e) => setLyricsText(e.target.value)}
                    placeholder="每行一句歌詞，例如：&#10;何もできなかったもうさんじゅうさん歳&#10;この気持ちもう何十何回&#10;急げ　じゃなくて　It's okay"
                />
            </Section>

            <Section>
                <Label>3. 選擇生成方法</Label>
                <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="whisper">OpenAI Whisper API (推薦，準確度高)</option>
                    <option value="estimation">時間估算方法 (簡單快速，無需 API Key)</option>
                    <option value="webspeech" disabled>Web Speech API (不適用於音頻文件)</option>
                </Select>
                {method === 'estimation' && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', borderRadius: '8px', fontSize: '0.9rem', color: '#856404' }}>
                        ⚠️ 時間估算方法會根據音頻時長和歌詞行數平均分配時間，可能不夠精確。建議使用 Whisper API 獲得更好的結果。
                    </div>
                )}
                {method === 'webspeech' && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#f8d7da', borderRadius: '8px', fontSize: '0.9rem', color: '#721c24' }}>
                        ❌ Web Speech API 無法直接處理音頻文件，需要從麥克風實時輸入。請選擇其他方法。
                    </div>
                )}
                {method === 'whisper' && (
                    <div style={{ marginTop: '10px' }}>
                        <Label>OpenAI API Key</Label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="sk-..."
                                />
                            </div>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleValidateApiKey}
                                disabled={isValidatingKey || !apiKey.trim()}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {isValidatingKey ? '驗證中…' : '驗證 API Key'}
                            </Button>
                        </div>
                        <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                            您的 API Key 僅在本地使用，不會上傳到服務器
                        </small>
                    </div>
                )}
            </Section>

            <Section>
                <Label>4. 元數據（可選）</Label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                        <Label>標題</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="歌曲標題"
                        />
                    </div>
                    <div>
                        <Label>藝術家</Label>
                        <Input
                            value={artist}
                            onChange={(e) => setArtist(e.target.value)}
                            placeholder="藝術家名稱"
                        />
                    </div>
                    <div>
                        <Label>專輯</Label>
                        <Input
                            value={album}
                            onChange={(e) => setAlbum(e.target.value)}
                            placeholder="專輯名稱"
                        />
                    </div>
                </div>
            </Section>

            <Section>
                <Button
                    onClick={handleGenerate}
                    disabled={isProcessing}
                >
                    {isProcessing && <LoadingSpinner />}
                    {isProcessing ? '處理中...' : '生成 LRC 文件'}
                </Button>
                {preview && (
                    <Button variant="secondary" onClick={handleDownload}>
                        下載 LRC 文件
                    </Button>
                )}
            </Section>

            {status.message && (
                <StatusMessage type={status.type}>
                    {status.message}
                </StatusMessage>
            )}

            {preview && (
                <Section>
                    <Label>預覽</Label>
                    <Preview>{preview}</Preview>
                </Section>
            )}
            </ContentWrapper>
        </Container>
    );
};

export default LRCGeneratorPage;

